import { BigNumber } from 'ethers'

export const OWNED_TOKEN_SCAN_CHUNK_SIZE = 500
export const STAKED_TOKEN_INDEX_BATCH_SIZE = 12
const FATSALE_START_TOKEN_ID = 1

const asBigNumber = (value) => BigNumber.from(value)

export async function loadOwnedTokenIds(contract, owner, options = {}) {
    const chunkSize = asBigNumber(options.chunkSize ?? OWNED_TOKEN_SCAN_CHUNK_SIZE)
    if (chunkSize.lte(0)) throw new Error('Owned token scan chunk size must be greater than zero')

    try {
        const tokenIds = await contract.tokensOfOwner(owner)
        if (!Array.isArray(tokenIds)) throw new Error('tokensOfOwner returned an invalid result')
        return { tokenIds, strategy: 'all' }
    } catch (fastPathError) {
        const expectedCount = asBigNumber(await contract.balanceOf(owner))
        if (expectedCount.isZero()) {
            return { tokenIds: [], strategy: 'ranges', fastPathError }
        }

        // FatSale V1/V2 token IDs start at 1 and cannot be burned, so the
        // current total supply plus one is the exclusive scan boundary.
        const stopExclusive = asBigNumber(await contract.totalSupply()).add(1)
        const tokenIds = []
        let start = asBigNumber(FATSALE_START_TOKEN_ID)

        while (start.lt(stopExclusive) && asBigNumber(tokenIds.length).lt(expectedCount)) {
            const candidateStop = start.add(chunkSize)
            const stop = candidateStop.lt(stopExclusive) ? candidateStop : stopExclusive

            try {
                const rangeTokenIds = await contract.tokensOfOwnerIn(owner, start, stop)
                if (!Array.isArray(rangeTokenIds)) throw new Error('tokensOfOwnerIn returned an invalid result')
                tokenIds.push(...rangeTokenIds)
            } catch (rangeError) {
                const error = new Error(`Unable to scan owned token IDs in range ${start.toString()}-${stop.toString()}`)
                error.cause = rangeError
                error.fastPathError = fastPathError
                throw error
            }

            start = stop
        }

        if (!asBigNumber(tokenIds.length).eq(expectedCount)) {
            const error = new Error(`Owned token scan incomplete: expected ${expectedCount.toString()} token IDs, received ${tokenIds.length}`)
            error.fastPathError = fastPathError
            throw error
        }

        return { tokenIds, strategy: 'ranges', fastPathError }
    }
}

export async function loadStakedTokenIds(contract, owner, options = {}) {
    try {
        const tokenIds = await contract.userAllStakeNFTID(owner)
        if (!Array.isArray(tokenIds)) throw new Error('userAllStakeNFTID returned an invalid result')
        return { tokenIds, strategy: 'all' }
    } catch (fastPathError) {
        const shouldFallback = options.shouldFallback || (() => true)
        if (!shouldFallback(fastPathError)) throw fastPathError

        const total = asBigNumber(await contract.inviterListsForUserTotal(owner))
        if (total.gt(asBigNumber(String(Number.MAX_SAFE_INTEGER)))) {
            throw new Error('Staked NFT count exceeds the supported pagination range')
        }

        const count = total.toNumber()
        const batchSize = options.batchSize ?? STAKED_TOKEN_INDEX_BATCH_SIZE
        if (!Number.isSafeInteger(batchSize) || batchSize <= 0) {
            throw new Error('Staked token index batch size must be a positive integer')
        }

        const tokenIds = []
        for (let start = 0; start < count; start += batchSize) {
            const stop = Math.min(count, start + batchSize)
            const batch = await Promise.all(
                Array.from(
                    { length: stop - start },
                    (_item, offset) => contract.inviterListsForUserPage(owner, start + offset),
                ),
            )
            tokenIds.push(...batch)
        }
        return { tokenIds, strategy: 'indexed', fastPathError }
    }
}
